import { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  title?: string;
  content: string;
  className?: string;
}

const TOOLTIP_WIDTH = 320;

export default function HelpTooltip({ title, content, className = '' }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const updatePos = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const nextY = rect.bottom + 6;
    const nextX = Math.min(
      Math.max(8, rect.left),
      Math.max(8, window.innerWidth - TOOLTIP_WIDTH - 8),
    );
    setPos({ x: nextX, y: nextY });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open, updatePos]);

  const hide = (): void => setOpen(false);

  return (
    <span className={`inline-flex ${className}`.trim()}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={title ? `More info: ${title}` : 'More info'}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={hide}
        onFocus={() => setOpen(true)}
        onBlur={hide}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-border text-xs leading-none text-accent border border-border hover:bg-surface2 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      >
        ?
      </button>
      {open && (
        <div
          className="fixed z-40 border border-border bg-surface2 text-text px-3 py-2 rounded text-[11px] leading-relaxed shadow-xl whitespace-normal break-words"
          style={{
            left: pos.x,
            top: pos.y,
            width: `min(${TOOLTIP_WIDTH}px, calc(100vw - 16px))`,
            overflowWrap: 'anywhere',
          }}
          role="tooltip"
        >
          {title && <div className="font-semibold text-accent mb-1">{title}</div>}
          <div>{content}</div>
        </div>
      )}
    </span>
  );
}
